import type React from 'react';
import clsx from 'clsx';
import { assign, flattenDeep, has, isEmpty, isEqual, isFunction, map, pick, xor } from 'lodash';
import type {
  AdvancedFormGroup,
  CollapsibleFormGroup,
  FormElement,
  FormGroup,
  FormGroupWithTitle,
  FormRow,
  RemovableFormGroup,
  RemovableFormRow,
  ValidatingFormComponent,
} from '@/formbuilder/formBuilder.constants';
import { SUPPORTED_COMPONENTS } from '@/formbuilder/formBuilder.constants';
import {
  AdvancedFormBlock,
  CollapsibleFormBlock,
  ConditionalFormBlock,
  FormBlock,
  FormGroupBlock,
  FormGroupWithTitleBlock,
  FormRowBlock,
  RemovableFormBlock,
  RemovableFormRowBlock,
} from '@/formbuilder/formBuildingBlocks';
import type { FormFieldWrapperProps } from './FormFieldWrapper';

export function getFormFieldProps(
  formState: { submitFailed?: boolean },
  input: { onChange?: (value: unknown) => void; value?: unknown },
  meta: { submitFailed?: boolean; error?: unknown; modified?: boolean },
  props: any,
): any {
  const { extraClassNames, testId, value, skipStore = false, defaultProvided = false } = props;
  const showError = Boolean(
    formState.submitFailed || meta.submitFailed ? meta.error : meta.error && (meta.modified || defaultProvided),
  );

  return {
    ...props,
    'onChange': (newValue: unknown) => {
      props.onChange?.(newValue);
      input.onChange?.(newValue);
    },
    'value': skipStore ? input.value : value,
    'data-testid': testId,
    // temporary until all Forms are converted to FormBuilder (https://seeq.atlassian.net/browse/CRAB-21727)
    'fromFormBuilder': true as const,
    'className': clsx({ 'error-border': showError }),
    'extraClassNames': clsx(extraClassNames, {
      'error-border': showError,
    }),
    showError,
  };
}

export function getFormFieldWrapperProps<T = unknown>(
  props: ValidatingFormComponent<T>,
  propsToOmit?: (keyof FormFieldWrapperProps)[],
): Omit<FormFieldWrapperProps, 'children'> {
  const propsToInclude = [
    'testId',
    'label',
    'wrapperClassNames',
    'customErrorText',
    'customErrorParams',
    'tooltip',
    'tooltipPlacement',
    'onTooltipClick',
    'popoverContent',
    'id',
    'warningText',
  ];
  const selectedProps = (propsToOmit?.length ?? 0) > 0 ? xor(propsToInclude, propsToOmit) : propsToInclude;

  return pick(props, selectedProps as (keyof ValidatingFormComponent<T>)[]) as Omit<FormFieldWrapperProps, 'children'>;
}

export function getValidationFunction<T>(
  defaultValidation: (value: T) => boolean,
  extendValidation: boolean | undefined,
  customValidation?: (value: T) => boolean,
) {
  if (extendValidation && isFunction(customValidation)) {
    return (value: T) => defaultValidation(value) || customValidation(value);
  }

  return isFunction(customValidation) ? customValidation : defaultValidation;
}

/**
 * Recursively "flattens" out all form elements into an array of form field names.
 **/
export function flattenFormDefinition(formDefinition: FormElement[]): FormElement[] {
  const flattenFormDefinitionInternal = (defs: FormElement[]): unknown[] =>
    map(defs, (def) =>
      (def.component === 'FormGroup' ||
        def.component === 'FormGroupWithTitle' ||
        def.component === 'AdvancedFormGroup' ||
        def.component === 'FormRow' ||
        def.component === 'RemovableFormRow' ||
        def.component === 'RemovableFormGroup' ||
        def.component === 'CollapsibleFormGroup') &&
      'components' in def &&
      def.components
        ? flattenFormDefinitionInternal(def.components)
        : def,
    );
  const flattened = flattenFormDefinitionInternal(formDefinition);
  return flattenDeep(flattened) as FormElement[];
}

export function decorateWithFieldNames(formDefinition: FormElement[]) {
  const addFieldNames = (formDefinition: FormElement[]): FormElement[] =>
    map(formDefinition, (def): FormElement => {
      const fieldNames = map(flattenFormDefinition([def]), 'name');
      const defWithChildren =
        'components' in def && !isEmpty((def as { components?: FormElement[] }).components)
          ? { ...def, components: addFieldNames((def as { components?: FormElement[] }).components as FormElement[]) }
          : def;

      return assign({}, defWithChildren, { fieldNames });
    });

  return addFieldNames(formDefinition);
}

// walks the formConfig and assigns the appropriate numbers for checkmarkAndNumber display
export function decorateWithNumbers(formDefinition: FormElement[]) {
  let number = 0;

  const hasDisplayNumber = (def: FormElement) =>
    def.displayNumber && (!has(def, 'includeIf') || (def as any).includeIf);

  const getDefWithNumber = (def: FormElement) => {
    number++;
    return assign({}, def, { number });
  };

  const addDisplayNumber = (formDefinition: FormElement[]): FormElement[] =>
    map(formDefinition, (def: FormElement) => {
      const hasComponents = 'components' in def && Array.isArray((def as { components?: FormElement[] }).components);
      if (hasDisplayNumber(def)) {
        const decoratedDef = getDefWithNumber(def);
        if (hasComponents && !isEmpty((def as { components?: FormElement[] }).components)) {
          assign(decoratedDef, {
            components: addDisplayNumber((def as { components?: FormElement[] }).components as FormElement[]),
          });
        }
        return decoratedDef;
      } else {
        if (
          hasComponents &&
          !isEmpty((def as { components?: FormElement[] }).components) &&
          (!has(def, 'includeIf') || (def as any).includeIf)
        ) {
          return assign({}, def, {
            components: addDisplayNumber((def as { components?: FormElement[] }).components as FormElement[]),
          });
        }
        return def;
      }
    });
  return addDisplayNumber(formDefinition);
}

export function renderIt(def: FormElement) {
  if (!def.component) {
    throw new Error('Every FormElement must provide a valid component or it can not be rendered.');
  }
  switch (def.component) {
    case 'FormGroup':
      return renderFormGroup(def as FormGroup);
    case 'FormGroupWithTitle':
      return renderFormGroupWithTitle(def as FormGroupWithTitle);
    case 'FormRow':
      return renderFormRow(def as FormRow);
    case 'RemovableFormRow':
      return renderRemovableFormRow(def as RemovableFormRow);
    case 'AdvancedFormGroup':
      return renderAdvancedFormGroup(def as AdvancedFormGroup);
    case 'RemovableFormGroup':
      return renderRemovableFormGroup(def as RemovableFormGroup);
    case 'CollapsibleFormGroup':
      return renderCollapsibleFormGroup(def as CollapsibleFormGroup);
    default:
      return renderFormComponent(def);
  }
}

function renderFormGroup(def: FormGroup) {
  const {
    key,
    name,
    components,
    testId,
    formGroupExtraClassNames,
    showBracket,
    showScroll = false,
    showGroupStyles,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <FormGroupBlock
      key={key}
      name={name}
      testId={testId}
      showGroupStyles={showGroupStyles}
      formGroupExtraClassNames={formGroupExtraClassNames}
      showBracket={showBracket}
      showScroll={showScroll}>
      {children}
    </FormGroupBlock>
  );

  return finalizeForRender(def, content);
}

function renderFormGroupWithTitle(def: FormGroupWithTitle) {
  const {
    key,
    name,
    components,
    testId,
    extraClassNames,
    showBracket,
    showScroll = false,
    showTitle = false,
    title,
    titleLink,
    titleLinkAction,
    additionalTitleLink,
    additionalTitleLinkAction,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <FormGroupWithTitleBlock
      key={key}
      name={name}
      testId={testId}
      extraClassNames={extraClassNames}
      showBracket={showBracket}
      showTitle={showTitle}
      title={title}
      titleLink={titleLink}
      titleLinkAction={titleLinkAction}
      additionalTitleLink={additionalTitleLink}
      additionalTitleLinkAction={additionalTitleLinkAction}
      showScroll={showScroll}>
      {children}
    </FormGroupWithTitleBlock>
  );

  return finalizeForRender(def, content);
}

function renderAdvancedFormGroup(def: AdvancedFormGroup) {
  const { key, name, components, testId, toolName, toolId, toolStore, alternateName, extraClassNames, iconStyle } = def;
  const children = components.map(renderIt);
  const content = (
    <AdvancedFormBlock
      key={key || name}
      name={name}
      testId={testId}
      toolName={toolName}
      toolId={toolId}
      toolStore={toolStore}
      alternateName={alternateName}
      extraClassNames={extraClassNames}
      iconStyle={iconStyle}>
      {children}
    </AdvancedFormBlock>
  );
  return finalizeForRender(def, content);
}

function renderCollapsibleFormGroup(def: CollapsibleFormGroup) {
  const {
    components,
    testId,
    deleteIconAction,
    hideDeleteIcon = false,
    extraClassNames,
    labelExtraClassNames,
    isCollapsed = false,
    toggleCollapse,
    label,
    isLast = false,
    isNewlyAdded = false,
    newLabel,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <CollapsibleFormBlock
      hideDeleteIcon={hideDeleteIcon}
      testId={testId}
      deleteIconAction={deleteIconAction}
      isCollapsed={isCollapsed}
      toggleCollapse={toggleCollapse}
      extraClassNames={extraClassNames}
      labelExtraClassNames={labelExtraClassNames}
      label={label}
      isLast={isLast}
      isNewlyAdded={isNewlyAdded}
      newLabel={newLabel}>
      {children}
    </CollapsibleFormBlock>
  );
  return finalizeForRender(def, content);
}

function renderRemovableFormGroup(def: RemovableFormGroup) {
  const {
    components,
    testId,
    iconAction,
    hideIcon = false,
    extraClassNames,
    iconHasMargin = false,
    iconTooltip,
    showLabelAfterRows,
    labelAfterRows,
    showAddBlockButton,
    addBlockButtonAction,
    addBlockButtonTooltip,
  } = def;
  const children = components.map(renderIt);
  const content = (
    <RemovableFormBlock
      hideIcon={hideIcon}
      testId={testId}
      iconAction={iconAction}
      iconHasMargin={iconHasMargin}
      iconTooltip={iconTooltip}
      showLabelAfterRows={showLabelAfterRows}
      labelAfterRows={labelAfterRows}
      showAddBlockButton={showAddBlockButton}
      addBlockButtonAction={addBlockButtonAction}
      addBlockButtonTooltip={addBlockButtonTooltip}
      extraClassNames={extraClassNames}>
      {children}
    </RemovableFormBlock>
  );
  return finalizeForRender(def, content);
}

function renderFormRow(def: FormRow) {
  const { name, components, testId, extraClassNames } = def;
  const children = components.map(renderIt);
  const content = (
    <FormRowBlock name={name} testId={testId} extraClassNames={clsx('formRow', extraClassNames)}>
      {children}
    </FormRowBlock>
  );

  return finalizeForRender(def, content);
}

function renderRemovableFormRow(def: RemovableFormRow) {
  const { name, components, testId, removeAction, hideRemoveIcon = false, extraClassNames, wrapperClassNames } = def;
  const children = components.map(renderIt);
  const content = (
    <RemovableFormRowBlock
      hideRemoveIcon={hideRemoveIcon}
      name={name}
      testId={testId}
      removeAction={removeAction}
      wrapperClassNames={wrapperClassNames}
      extraClassNames={clsx('formRow', extraClassNames)}>
      {children}
    </RemovableFormRowBlock>
  );
  return finalizeForRender(def, content);
}

function renderFormComponent(def: FormElement) {
  const Component = (SUPPORTED_COMPONENTS as Record<string, React.ComponentType<unknown>>)[def.component];
  if (!Component) {
    throw new Error(`Please supply an existing FormComponent or add ${def.component} as a supported component.`);
  }

  return renderWrapped(Component, def);
}

function renderWrapped(What: React.ComponentType<unknown>, props: FormElement) {
  const { name } = props;
  return finalizeForRender(props, <What {...props} key={name} />);
}

function finalizeForRender(def: FormElement, content: React.JSX.Element) {
  const { number, includeIf, name, fieldNames, key, formBlockClassNames } = def;
  const finalContent: React.JSX.Element = number ? (
    <FormBlock name={name} formBlockClassNames={formBlockClassNames}>
      {content}
    </FormBlock>
  ) : (
    content
  );

  return (
    <ConditionalFormBlock key={key || name} isConditional={has(def, 'includeIf')} show={includeIf} name={name}>
      {finalContent}
    </ConditionalFormBlock>
  );
}

type MemoComparableProps = {
  value?: unknown;
  disabled?: boolean;
  extendValidation?: boolean;
  extraClassNames?: string;
  options?: unknown;
};

export function canUseMemo(prev: MemoComparableProps, next: MemoComparableProps) {
  // return false if render is necessary
  return !(
    !isEqual(prev.value, next.value) ||
    prev.disabled !== next.disabled ||
    prev.extendValidation ||
    prev.extraClassNames !== next.extraClassNames ||
    prev.options !== next.options
  );
}
